<template>
  <div class="fixed z-10 bg-smoke-300 inset-0 w-full h-full">
    <div class="lg:p-12 h-full min-h-full">
      <div
        class="lg:rounded lg:shadow-lg max-w-full mx-auto bg-gray-300 p-2 h-full text-sm overflow-auto fancy-scrollbar"
      >
        <div v-if="!loading && contactRequest && !deleting" class="flex flex-col h-full">
          <div class="flex-none">
            <div class="p-4 bg-gray-300 rounded-t">
              <div class="flex -mx-4">
                <div class="flex-1 px-3 flex truncate">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    class="fill-current text-orange-600"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0)">
                      <path
                        d="M31.6667 0H8.33337C7.41671 0 6.66671 0.75 6.66671
                        1.66667C6.66671 2.58333 7.41671 3.33333 8.33337
                        3.33333H31.6667C32.5834 3.33333 33.3334 2.58333
                        33.3334 1.66667C33.3334 0.75 32.5834 0 31.6667
                        0ZM8.33337 40H31.6667C32.5834 40 33.3334 39.25
                        33.3334 38.3333C33.3334 37.4167 32.5834 36.6667
                        31.6667 36.6667H8.33337C7.41671 36.6667 6.66671
                        37.4167 6.66671 38.3333C6.66671 39.25 7.41671 40
                        8.33337 40ZM33.3334 6.66667H6.66671C4.83337 6.66667
                        3.33337 8.16667 3.33337 10V30C3.33337 31.8333 4.83337
                        33.3333 6.66671 33.3333H33.3334C35.1667 33.3333 36.6667
                        31.8333 36.6667 30V10C36.6667 8.16667 35.1667 6.66667
                        33.3334 6.66667ZM20 11.25C22.0667 11.25 23.75 12.9333
                        23.75 15C23.75 17.0667 22.0667 18.75 20 18.75C17.9334
                        18.75 16.25 17.0667 16.25 15C16.25 12.9333 17.9334 11.25
                        20 11.25ZM28.3334 28.3333H11.6667V25.8333C11.6667 23.05
                        17.2167 21.6667 20 21.6667C22.7834 21.6667 28.3334 23.05
                        28.3334 25.8333V28.3333Z"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0">
                        <rect width="40" height="40" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <div class="ml-2 truncate">
                    <h5 class="text-sm font-medium text-gray-600 leading-4">Contact Request</h5>
                    <div class="flex space-x-2">
                      <h3 class="text-2xl leading-6 font-bold truncate">
                        {{ contactRequest.fullName }}
                      </h3>
                      <BaseButton small ghost v-if="contactRequest.contactId" @click="showContact">
                        Show Contact
                      </BaseButton>
                    </div>
                    <p class="text-gray-600 text-xs leading-5 block">
                      <span>{{ createdAtFromNow }}</span>
                      &bull; {{ createdAt }}
                    </p>
                  </div>
                </div>
                <div class="flex-none px-4 ml-auto hidden lg:block">
                  <UnsubscribeAction :contact-request="contactRequest" />
                  <button
                    v-if="$membershipHasPermissionTo('delete contact requests')"
                    class="bg-red-600 text-white px-4 py-1 rounded ml-2 leading-6 hover:bg-red-700 focus:outline-none"
                    :disabled="deleting"
                    @click="displayDeleteConfirmation = true"
                  >
                    <FontAwesomeIcon icon="trash" fixed-width />
                    Delete
                  </button>
                  <button
                    class="bg-transparent text-blue-600 px-4 py-1 rounded ml-2 leading-6 hover:bg-blue-600 hover:text-white focus:outline-none"
                    :disabled="deleting"
                    @click="doCloseAction"
                  >
                    <FontAwesomeIcon icon="times" fixed-width />
                    Close
                  </button>
                </div>
                <div class="lg:hidden flex-none ml-auto px-4">
                  <VDropdown right>
                    <template #button="buttonProps">
                      <BaseButton primary @click="buttonProps.open"> Menu </BaseButton>
                    </template>

                    <template #default="defaultProps">
                      <VDropdownMenu>
                        <button
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('DetailsTab'))"
                        >
                          Details
                        </button>
                        <button
                          v-if="$membershipHasPermissionTo('read tasks')"
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Activity'))"
                        >
                          Tasks
                        </button>
                        <button
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Notes'))"
                        >
                          Notes
                        </button>
                        <button
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Tags'))"
                        >
                          Tags
                        </button>
                        <button
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Attachments'))"
                        >
                          Attachments
                        </button>
                        <button
                          v-if="isAdmin"
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Offers'))"
                        >
                          Offers
                        </button>
                        <button
                          v-if="isAdmin"
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('Messages'))"
                        >
                          Messages
                        </button>
                        <button
                          v-if="isAdmin"
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => setCurrentTab('ConversationManager'))"
                        >
                          Conversation
                        </button>
                        <hr class="my-2" />
                        <button
                          role="button"
                          :class="[
                            'block',
                            'px-4',
                            'py-2',
                            'hover:bg-gray-200',
                            'transition-color',
                            'transition-fast',
                            'text-right',
                            'text-sm',
                            'w-full',
                          ]"
                          @click="defaultProps.close(() => confirmDelete())"
                        >
                          Delete
                        </button>
                      </VDropdownMenu>
                    </template>
                  </VDropdown>

                  <BaseButton primary @click="doCloseAction" class="ml-2">
                    <FontAwesomeIcon icon="times" fixed-width />
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-none px-4">
            <div class="bg-white px-4 rounded p-4">
              <div class="flex -mx-4">
                <Project :contact-request="contactRequest" />
                <PipelineStage :contact-request="contactRequest" />
              </div>
            </div>
          </div>
          <div class="flex-1 mt-3 lg:my-0">
            <div class="bg-gray-300 h-full min-h-full p-4">
              <div class="flex flex-col lg:flex-row -mx-4 h-full min-h-full">
                <div class="w-full mb-2 lg:mb-0 lg:w-64 px-4 border-r border-gray-400">
                  <DialogMenu :current-tab="currentTab" @change="currentTab = $event">
                    <AssignManager :contact-request="contactRequest" />
                  </DialogMenu>
                </div>
                <div class="flex-1 px-2 lg:h-full lg:min-h-full mt-6 lg:mt-0">
                  <component :is="currentTab" :contact-request="contactRequest" editable />
                </div>
                <div class="flex-1 lg:flex-none lg:w-1/3 mt-8 lg:mt-0 px-2 lg:px-4">
                  <CommentManager :contact-request="contactRequest" />
                </div>
              </div>
            </div>
          </div>
          <portal v-if="displayDeleteConfirmation" to="app-dialog">
            <BaseDialogDelete
              danger
              :custom-classes="'max-w-md'"
              @closed="displayDeleteConfirmation = false"
              @confirmed="doDelete"
            />
          </portal>
        </div>
        <div v-else class="flex h-full">
          <Loader class="m-auto" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import VDropdown from '@/components/Common/VDropdown.vue';
import VDropdownMenu from '@/components/Common/VDropdownMenu.vue';
import Project from './contactRequestModal/Project.vue';
import PipelineStage from './contactRequestModal/PipelineStage.vue';
import Activity from './contactRequestModal/Activity.vue';
import AssignManager from './contactRequestModal/AssignManager.vue';
import Attachments from './contactRequestModal/Attachments.vue';
import CommentManager from './contactRequestModal/CommentManager.vue';
import DetailsTab from './contactRequestModal/DetailsTab.vue';
import ConversationManager from './contactRequestModal/ConversationManager.vue';
import DialogMenu from './contactRequestModal/Menu.vue';
import Notes from './contactRequestModal/Notes.vue';
import Offers from './contactRequestModal/Offers.vue';
import Tags from './contactRequestModal/Tags.vue';
import PersonManager from './contactRequestModal/PersonManager.vue';
import Messages from './contactRequestModal/Messages.vue';
import UnsubscribeAction from './contactRequestModal/UnsubscribeAction.vue';

export default {
  name: 'ContactRequestModal',
  components: {
    Project,
    PipelineStage,
    Activity,
    AssignManager,
    Attachments,
    ConversationManager,
    CommentManager,
    DetailsTab,
    DialogMenu,
    Notes,
    Offers,
    Tags,
    VDropdown,
    VDropdownMenu,
    PersonManager,
    Messages,
    UnsubscribeAction,
  },

  props: {
    contactRequestId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      currentTab: this.$route.query.taskId ? 'Activity' : 'DetailsTab',
      displayDeleteConfirmation: false,
      deleting: false,
      loading: false,
    };
  },

  computed: {
    contactRequest() {
      return this.$store.state.entities.contactRequests.byId[this.contactRequestId];
    },
    isAdmin() {
      return this.$isAdminOrSuperAdmin;
    },
    createdAt() {
      return moment.utc(this.contactRequest.createdAt).local().format('YYYY-MM-DD HH:mm');
    },
    createdAtFromNow() {
      return moment.utc(this.contactRequest.createdAt).local().fromNow();
    },
  },
  watch: {
    '$route.path': {
      handler() {
        this.doCloseAction();
      },
    },
    contactRequest: {
      handler(newValue, oldValue) {
        if (!newValue || !oldValue) {
          return;
        }
        if (newValue.pipelineStageId !== oldValue.pipelineStageId) {
          this.$emit('pipeline-stage-change', {
            contactRequestId: newValue.id,
            to: newValue.pipelineStageId,
            from: oldValue.pipelineStageId,
          });
        }
      },
    },
  },
  created() {
    this.doInitAction();
  },
  methods: {
    setCurrentTab(name) {
      this.currentTab = name;
    },
    showContact() {
      this.$modal.show({
        component: 'ContactModal',
        props: {
          contactId: this.contactRequest.contactId,
        },
        hideEvent: 'hide',
      });
    },
    confirmDelete() {
      this.displayDeleteConfirmation = true;
    },
    async doInitAction() {
      try {
        this.loading = true;
        await this.readContactRequest();
        await this.loadAccountMembers();
      } catch (error) {
        this.doCloseAction();
      } finally {
        this.loading = false;
      }
    },
    async readContactRequest() {
      try {
        await this.$store.dispatch('ui/contactRequests/read', {
          id: this.contactRequestId,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async loadAccountMembers() {
      try {
        await this.$store.dispatch('ui/accountMembers/load', {
          accountId: this.contactRequest.accountId,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    doCloseAction() {
      this.$emit('hide');
    },
    async doDelete() {
      this.deleting = true;

      try {
        await this.$store.dispatch('ui/contactRequests/destroy', this.contactRequestId);

        this.$emit('destroyed', this.contactRequestId);

        this.doCloseAction();
      } catch (error) {
        // silence
      }

      this.deleting = false;
    },
  },
  provide() {
    return {
      refresh: () => {
        this.readContactRequest();
      },
    };
  },
};
</script>
